import { useState, useEffect } from 'react'
import { EducationalCard } from './EducationalCard'
import { EducationProgress } from './EducationProgress'
import { SeniorButton } from './SeniorButton'
import { educationalCards } from '../data/educationalCards'
import { useAppStore } from '../store/appStore'
import { EducationalCard as EducationalCardType } from '../types'
import { useAccessibility } from '../contexts/AccessibilityContext'

export function EducationModule() {
  const { settings } = useAccessibility()
  const { currentSession, updateSession } = useAppStore()
  const [cards, setCards] = useState<EducationalCardType[]>(educationalCards)
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all')

  // Load progress from session
  useEffect(() => {
    if (currentSession?.educationProgress) {
      setCards(prevCards => 
        prevCards.map(card => ({
          ...card,
          completed: currentSession.educationProgress.includes(card.id)
        }))
      )
    }
  }, [currentSession])

  const handleCardComplete = (cardId: string) => {
    // Update local state
    setCards(prevCards => 
      prevCards.map(card => 
        card.id === cardId ? { ...card, completed: true } : card
      )
    )

    // Update session progress
    const newProgress = currentSession?.educationProgress || []
    if (!newProgress.includes(cardId)) {
      updateSession({
        educationProgress: [...newProgress, cardId]
      })
    }
  }

  const filteredCards = selectedDifficulty === 'all' 
    ? cards 
    : cards.filter(card => card.difficulty === selectedDifficulty)

  const completedCount = filteredCards.filter(card => card.completed).length
  const totalCount = filteredCards.length

  const getTitleSize = () => {
    switch (settings.fontSize) {
      case 'small': return 'text-2xl'
      case 'medium': return 'text-3xl'
      case 'large': return 'text-4xl'
      case 'extra-large': return 'text-5xl'
    }
  }

  const getSubtitleSize = () => {
    switch (settings.fontSize) {
      case 'small': return 'text-lg'
      case 'medium': return 'text-xl'
      case 'large': return 'text-2xl'
      case 'extra-large': return 'text-3xl'
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className={`${getTitleSize()} font-bold text-gray-900 mb-6`}>
          Как устроена умная машина
        </h2>
        <p className={`${getSubtitleSize()} text-gray-600 max-w-3xl mx-auto leading-relaxed`}>
          Узнаем, как работает машина, которая может отвечать на вопросы, через простые примеры из жизни
        </p>
      </div>

      {/* Difficulty filter */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
          Выберите уровень сложности:
        </h3>
        <div 
          className="flex flex-wrap justify-center gap-4"
          role="tablist"
          aria-label="Выбор уровня сложности материала"
        >
          <SeniorButton
            onClick={() => setSelectedDifficulty('all')}
            variant={selectedDifficulty === 'all' ? 'primary' : 'secondary'}
            size="medium"
            role="tab"
            aria-selected={selectedDifficulty === 'all'}
            aria-controls="education-cards"
            icon="📚"
          >
            Все темы
          </SeniorButton>
          <SeniorButton
            onClick={() => setSelectedDifficulty('beginner')}
            variant={selectedDifficulty === 'beginner' ? 'primary' : 'secondary'}
            size="medium"
            role="tab"
            aria-selected={selectedDifficulty === 'beginner'}
            aria-controls="education-cards"
            icon="🌱"
          >
            Начальный
          </SeniorButton>
          <SeniorButton
            onClick={() => setSelectedDifficulty('intermediate')}
            variant={selectedDifficulty === 'intermediate' ? 'primary' : 'secondary'}
            size="medium"
            role="tab"
            aria-selected={selectedDifficulty === 'intermediate'}
            aria-controls="education-cards"
            icon="🌿"
          >
            Средний
          </SeniorButton>
          <SeniorButton
            onClick={() => setSelectedDifficulty('advanced')}
            variant={selectedDifficulty === 'advanced' ? 'primary' : 'secondary'}
            size="medium"
            role="tab"
            aria-selected={selectedDifficulty === 'advanced'}
            aria-controls="education-cards"
            icon="🌳"
          >
            Сложный
          </SeniorButton>
        </div>
      </div>

      {/* Progress tracking */}
      <EducationProgress completed={completedCount} total={totalCount} />

      {/* Educational cards */}
      <div 
        id="education-cards"
        className="space-y-6"
        role="tabpanel"
        aria-label="Список образовательных карточек"
      >
        {filteredCards.length > 0 ? (
          filteredCards.map(card => (
            <EducationalCard 
              key={card.id}
              card={card}
              onComplete={handleCardComplete}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <p className={`${getSubtitleSize()} text-gray-500`}>
              Материалов для выбранного уровня пока нет.
            </p>
          </div>
        )}
      </div>

      {/* Completion message */}
      {completedCount === totalCount && totalCount > 0 && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8 mt-12 text-center">
          <h3 className={`${getTitleSize()} font-semibold text-green-900 mb-4`}>
            🎉 Превосходно!
          </h3>
          <p className={`${getSubtitleSize()} text-green-700 leading-relaxed`}>
            Вы изучили все материалы в этой категории. Теперь вы лучше понимаете, как работает умная машина!
          </p>
        </div>
      )}
    </div>
  )
}